import { remediation_rows } from './controls_data';
import { control_drawer_blueprints, control_drawer_chip, control_drawer_record, control_drawer_test } from './controls_drawer_data_b';

function remediation_tone_from_status_class(status_class) {
  if (status_class === 'is-passed') return 'green';
  if (status_class === 'is-failed') return 'red';
  return 'amber';
}

function value_or_default(value, fallback_value) {
  const resolved_value = value || fallback_value;
  return resolved_value;
}

function build_control_drawer_tests(detail_status, control_subtitle, linked_risk, last_tested) {
  const control_tests = [
    control_drawer_test(
      detail_status,
      control_subtitle || linked_risk,
      last_tested || '-',
      'slate'
    )
  ];
  return control_tests;
}

function build_control_details(detail_status, detail_value_class, frequency, last_tested, next_due) {
  const control_details = [
    { label: 'Result', value: detail_status, value_class: detail_value_class },
    { label: 'Frequency', value: frequency },
    { label: 'Last tested', value: last_tested || '-' },
    { label: 'Next due', value: next_due || frequency }
  ];
  return control_details;
}

function build_blueprint_defaults(id, category, control, linked_risk) {
const controlLinkedArtifacts = {
  risks: [control_drawer_chip(linked_risk, 'amber')],
  obligations: [control_drawer_chip(`${id} - ${control}`, 'teal')],
  policies: [control_drawer_chip(`${category} policy`, 'teal')],
  evidence: [control_drawer_chip('Evidence pack', 'document')],
  escalation: [control_drawer_record('Risk register', linked_risk, 'Linked risk')]
};

return controlLinkedArtifacts;
}

function blueprint_or_default(blueprint, key, fallback_value) {
  const blueprint_value = Reflect.get(blueprint || {}, key);
  return blueprint_value || fallback_value;
}

function remediation_related_actions(row) {
  const related_actions = remediation_rows
    .filter((item) => item.control === row.control)
    .map((item) => control_drawer_record('Remediation', item.action, `${item.owner} - due ${item.due}`, item.id === row.id ? 'amber' : remediation_tone_from_status_class(item.status?.class)));
  return related_actions;
}

function remediation_control_result(control_row) {
  const control_result = control_row.result || {};
  const remediation_result = {
    label: control_result.label || 'Not tested',
    class: control_result.class || 'is-muted'
  };
  return remediation_result;
}

function remediation_details(row, control_row, status_label, status_class) {
  const control_result = remediation_control_result(control_row);
  const details = [
    { label: 'Status', value: status_label, value_class: status_class },
    { label: 'Owner', value: row.owner || 'TBC' },
    { label: 'Due', value: row.due || '-' },
    { label: 'Control result', value: control_result.label, value_class: control_result.class }
  ];
  return details;
}

function remediation_tests(blueprint, row, status_label, status_class) {
  const blueprint_tests = blueprint.tests || [];
  const tests = [control_drawer_test(status_label, `${row.action} - ${row.owner}`, row.due || '-', remediation_tone_from_status_class(status_class)), ...blueprint_tests];
  return tests;
}

function remediation_escalation(blueprint, row, status_class) {
  const blueprint_escalation = blueprint.escalation || [];
  const escalation = [control_drawer_record('Corrective action', row.action, `${row.owner} - due ${row.due}`, remediation_tone_from_status_class(status_class)), ...blueprint_escalation];
  return escalation;
}

function build_control_drawer_payload(row, blueprint, detail_status, detail_value_class, freq) {
  const { id, category, control, owner, frequency, last_tested, next_due, control_subtitle, linked_risk } = row;
  const blueprint_defaults = build_blueprint_defaults(id, category, control, linked_risk);
  const control_details= {
    key: id,
    row,
    section: `CONTROL - ${id} - ${category.toUpperCase()}`,
    title: control,
    subtitle: `${owner} - tested ${freq}`,
    status: detail_status,
    status_class: detail_value_class,
    details: build_control_details(detail_status, detail_value_class, frequency, last_tested, next_due),
    summary: blueprint_or_default(blueprint, 'summary', control_subtitle || linked_risk),
    objective: blueprint_or_default(blueprint, 'objective', `Control objective for ${control}`),
    connected_records: blueprint_or_default(blueprint, 'connected_records', []),
    risks: blueprint_or_default(blueprint, 'risks', blueprint_defaults.risks),
    obligations: blueprint_or_default(blueprint, 'obligations', blueprint_defaults.obligations),
    policies: blueprint_or_default(blueprint, 'policies', blueprint_defaults.policies),
    evidence: blueprint_or_default(blueprint, 'evidence', blueprint_defaults.evidence),
    tests: blueprint_or_default(blueprint, 'tests', build_control_drawer_tests(detail_status, control_subtitle, linked_risk, last_tested)),
    escalation: blueprint_or_default(blueprint, 'escalation', blueprint_defaults.escalation),
    remediation: blueprint_or_default(blueprint, 'remediation', []),
    footer_hint: blueprint_or_default(blueprint, 'footer_hint', '')
  };
  return control_details;
}

export function build_control_drawer(row) {
  if (!row) return null;
  const blueprint = Reflect.get(control_drawer_blueprints, row.id) || {};
  const detail_status = value_or_default(row.result?.label, 'Not tested');
  const detail_value_class = value_or_default(row.result?.class, 'is-muted');
  const freq = value_or_default(row.frequency?.toLowerCase?.(), row.frequency);
  const control_drawer = build_control_drawer_payload(row, blueprint, detail_status, detail_value_class, freq);
  return control_drawer;
}

function remediation_drawer_defaults(blueprint, row, control_row) {
  const remediation_defaults = {
    summary: value_or_default(blueprint.summary, row.subtitle),
    connected_records: value_or_default(blueprint.connected_records, []),
    risks: value_or_default(blueprint.risks, [control_drawer_chip(control_row.linked_risk, 'amber')]),
    obligations: value_or_default(blueprint.obligations, []),
    policies: value_or_default(blueprint.policies, []),
    evidence: value_or_default(blueprint.evidence, [])
  };
  return remediation_defaults;
}

export function build_remediation_drawer(row, control_row) {
  if (!row || !control_row) return null;
  const blueprint = Reflect.get(control_drawer_blueprints, control_row.id) || {};
  const related_actions = remediation_related_actions(row);
  const status_label = value_or_default(row.status?.label, 'Open');
  const status_class = value_or_default(row.status?.class, 'is-warning');
  const defaults = remediation_drawer_defaults(blueprint, row, control_row);
  const remediation_drawer = {
    key: row.id,
    kind: 'remediation',
    row: control_row,
    remediation_row: row,
    section: `REMEDIATION - ${control_row.id} - ${control_row.category.toUpperCase()}`,
    title: row.action,
    subtitle: `${control_row.control} - ${row.owner} owner`,
    status: status_label,
    status_class,
    details: remediation_details(row, control_row, status_label, status_class),
    summary: defaults.summary,
    objective: `Close the gap created by ${control_row.control}.`,
    connected_records: defaults.connected_records,
    risks: defaults.risks,
    obligations: defaults.obligations,
    policies: defaults.policies,
    evidence: defaults.evidence,
    tests: remediation_tests(blueprint, row, status_label, status_class),
    escalation: remediation_escalation(blueprint, row, status_class),
    remediation: related_actions,
    footer_hint: value_or_default(row.subtitle, '')
  };
  return remediation_drawer;
}

export const drawer_computed = {
  active_control_drawer() {
    if (!this.active_control_drawer_key) return null;
    const row = this.register_rows.find((item) => item.id === this.active_control_drawer_key);
    return row ? build_control_drawer(row) : null;
  },
  active_remediation_drawer() {
    if (!this.active_remediation_drawer_key) return null;
    const remediation_row = this.remediation_rows.find((item) => item.id === this.active_remediation_drawer_key);
    if (!remediation_row) return null;
    const control_row = this.register_rows.find((item) => item.id === remediation_row.control);
    return control_row ? build_remediation_drawer(remediation_row, control_row) : null;
  },
  active_library_drawer() {
    return this.active_remediation_drawer || this.active_control_drawer;
  }
};
